import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { apiGet } from "../lib/api.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DocumentChunk {
	id: string;
	chunkIndex: number;
	content: string;
	/** Similarity score if this chunk was matched (0–1). */
	similarity?: number;
}

interface DocumentChunksResponse {
	chunks: DocumentChunk[];
}

interface SourceDocumentModalProps {
	documentId: string;
	fileName: string;
	workdriveFileId?: string;
	/** Indices of chunks that were matched in the RAG retrieval (for highlighting). */
	matchedChunkIndices?: number[];
	onClose: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Derives a short, human-readable file type label from the filename extension. */
function getFileType(fileName: string): string {
	const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
	const map: Record<string, string> = {
		pdf: "PDF",
		docx: "Word",
		doc: "Word",
		txt: "Text",
		vtt: "Transcript",
		msg: "Email",
		xlsx: "Excel",
		xls: "Excel",
		pptx: "PowerPoint",
		ppt: "PowerPoint",
	};
	return (map[ext] ?? ext.toUpperCase()) || "Document";
}

/** Maps a file type label to a background/text colour pair for the badge. */
function getFileTypeBadgeClass(fileType: string): string {
	switch (fileType) {
		case "PDF": return "source-modal-type-badge--pdf";
		case "Word": return "source-modal-type-badge--word";
		case "Transcript": return "source-modal-type-badge--transcript";
		case "Email": return "source-modal-type-badge--email";
		case "Text": return "source-modal-type-badge--text";
		default: return "source-modal-type-badge--default";
	}
}

// ---------------------------------------------------------------------------
// Close button icon
// ---------------------------------------------------------------------------

function CloseIcon() {
	return (
		<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="14" height="14">
			<path d="M2 2L12 12M12 2L2 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
		</svg>
	);
}

// ---------------------------------------------------------------------------
// Spinner
// ---------------------------------------------------------------------------

function Spinner() {
	return (
		<div className="source-modal-loading" role="status" aria-label="Loading document chunks">
			<div className="source-modal-spinner" aria-hidden="true" />
			<span>Loading document…</span>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Portal-based modal that displays all text chunks for a source document.
 * Matched chunks (those retrieved by RAG) are visually highlighted.
 *
 * Dismissal: Escape key, backdrop click, or X button.
 * Footer shows "View in WorkDrive" link when a workdriveFileId is available.
 */
export default function SourceDocumentModal({
	documentId,
	fileName,
	workdriveFileId,
	matchedChunkIndices = [],
	onClose,
}: SourceDocumentModalProps) {
	const [chunks, setChunks] = useState<DocumentChunk[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Fetch chunks on mount
	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(null);

		apiGet(`/api/documents/${documentId}/chunks`)
			.then(async (res) => {
				if (cancelled) return;
				if (!res.ok) {
					const body = await res.json().catch(() => null) as { message?: string } | null;
					throw new Error(body?.message ?? `Failed to load document (${res.status})`);
				}
				const data = await res.json() as DocumentChunksResponse;
				if (!cancelled) {
					setChunks(data.chunks ?? []);
					setLoading(false);
				}
			})
			.catch((err: unknown) => {
				if (cancelled) return;
				const message = err instanceof Error ? err.message : "Failed to load document chunks.";
				setError(message);
				setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [documentId]);

	// Escape key dismissal
	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		},
		[onClose],
	);

	useEffect(() => {
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [handleKeyDown]);

	// Backdrop click
	const handleBackdropClick = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			if (e.target === e.currentTarget) onClose();
		},
		[onClose],
	);

	const fileType = getFileType(fileName);
	const badgeClass = getFileTypeBadgeClass(fileType);
	const matchedSet = new Set(matchedChunkIndices);

	const modal = (
		<div
			className="source-modal-backdrop"
			onClick={handleBackdropClick}
			role="dialog"
			aria-modal="true"
			aria-labelledby="source-modal-title"
		>
			<div className="source-modal">
				{/* Header */}
				<div className="source-modal-header">
					<div className="source-modal-header-left">
						<span className={`source-modal-type-badge ${badgeClass}`}>{fileType}</span>
						<h2 className="source-modal-filename" id="source-modal-title" title={fileName}>
							{fileName}
						</h2>
					</div>
					<button
						className="source-modal-close-btn"
						onClick={onClose}
						aria-label="Close document viewer"
					>
						<CloseIcon />
					</button>
				</div>

				{/* Body */}
				<div className="source-modal-content" role="region" aria-label="Document content">
					{loading && <Spinner />}

					{!loading && error && (
						<div className="source-modal-error" role="alert">
							<p>Unable to load document content.</p>
							<p className="source-modal-error-detail">{error}</p>
						</div>
					)}

					{!loading && !error && chunks.length === 0 && (
						<div className="source-modal-empty">
							<p>No content available for this document.</p>
						</div>
					)}

					{!loading && !error && chunks.length > 0 && (
						<ol className="source-modal-chunk-list" aria-label="Document chunks">
							{chunks.map((chunk) => {
								const isMatched = matchedSet.has(chunk.chunkIndex);
								return (
									<li
										key={chunk.id}
										className={`source-modal-chunk${isMatched ? " source-modal-chunk--matched" : ""}`}
									>
										<div className="source-modal-chunk-header">
											<span className="source-modal-chunk-index" aria-label={`Chunk ${chunk.chunkIndex + 1}`}>
												#{chunk.chunkIndex + 1}
											</span>
											{isMatched && chunk.similarity !== undefined && (
												<span
													className="source-modal-similarity"
													title={`Relevance score: ${Math.round(chunk.similarity * 100)}%`}
												>
													{Math.round(chunk.similarity * 100)}% match
												</span>
											)}
											{isMatched && chunk.similarity === undefined && (
												<span className="source-modal-similarity source-modal-similarity--matched">
													Referenced
												</span>
											)}
										</div>
										<p className="source-modal-chunk-text">{chunk.content}</p>
									</li>
								);
							})}
						</ol>
					)}
				</div>

				{/* Footer */}
				{workdriveFileId && (
					<div className="source-modal-footer">
						<a
							href={`https://workdrive.zoho.eu/file/${workdriveFileId}`}
							target="_blank"
							rel="noopener noreferrer"
							className="source-modal-workdrive-btn"
						>
							{/* External link icon */}
							<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="13" height="13">
								<path d="M5.5 2H2a1 1 0 00-1 1v9a1 1 0 001 1h9a1 1 0 001-1V8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
								<path d="M8 1h5v5M13 1L7 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
							View in WorkDrive
						</a>
					</div>
				)}
			</div>
		</div>
	);

	return createPortal(modal, document.body);
}
